import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import { isClinicFeatureEnabled } from '../../../modules/clinicFeatures/clinicFeatureUtils';
import { getNavTheme, sidebarItems } from '../../../navigation/clinicNavConfig';
import './EcxSidebar.css';

const getTheme = getNavTheme;

const getVisibleSidebarItems = (clinicFeatures, clinicFeaturesLoaded) =>
    sidebarItems
        .map((item) => {
            if (!item.submenu) {
                return item;
            }

            const submenu = item.submenu.filter((sub) =>
                isClinicFeatureEnabled(sub.featureKey, clinicFeatures, clinicFeaturesLoaded)
            );

            if (submenu.length === 0) {
                return null;
            }

            return { ...item, submenu };
        })
        .filter(Boolean)
        .filter((item) => isClinicFeatureEnabled(item.featureKey, clinicFeatures, clinicFeaturesLoaded));

function NavIcon({ icon, themeKey, collapsed }) {
    const theme = getTheme(themeKey);

    return (
        <span className={`ecx-nav-icon ${theme.iconClass} ${collapsed ? '' : 'me-2'}`}>
            <i className={`bi ${icon}`}></i>
        </span>
    );
}

function NavItem({ to, icon, label, themeKey, collapsed, badge, onNavigate }) {
    const theme = getTheme(themeKey);
    const tooltipId = `ecx-nav-${to.replace(/\//g, '-')}`;

    return (
        <li className="nav-item">
            <NavLink
                to={to}
                className={({ isActive }) =>
                    `nav-link rounded-3 d-flex align-items-center py-2 px-2 fw-medium ${
                        collapsed ? 'justify-content-center position-relative' : ''
                    } ${isActive ? theme.activeClass : theme.idleClass}`
                }
                onClick={onNavigate}
                data-tooltip-id={collapsed ? tooltipId : undefined}
                data-tooltip-content={collapsed ? label : undefined}
                data-tooltip-place="right"
            >
                <NavIcon icon={icon} themeKey={themeKey} collapsed={collapsed} />
                {!collapsed && <span>{label}</span>}
                {badge > 0 && (
                    <span
                        className={`badge rounded-pill bg-success ${
                            collapsed ? 'position-absolute top-0 end-0' : 'ms-auto'
                        }`}
                        style={{ fontSize: '0.65rem' }}
                    >
                        {badge}
                    </span>
                )}
            </NavLink>
            {collapsed && <Tooltip id={tooltipId} />}
        </li>
    );
}

function SubNavIcon({ icon, themeKey }) {
    const theme = getTheme(themeKey);

    return (
        <span className={`ecx-nav-subicon ${theme.iconClass}`}>
            <i className={`bi ${icon}`}></i>
        </span>
    );
}

function SubmenuLink({ sub, themeKey, className, onClick }) {
    return (
        <NavLink
            to={sub.to}
            className={className}
            onClick={onClick}
        >
            <SubNavIcon icon={sub.icon} themeKey={themeKey} />
            <span className="ecx-submenu-label">{sub.label}</span>
        </NavLink>
    );
}

function SubmenuItem({
    item,
    collapsed,
    openSubmenus,
    toggleSubmenu,
    hoveredSubmenu,
    setHoveredSubmenu,
    submenuPosition,
    setSubmenuPosition,
    location,
    onNavigate,
}) {
    const theme = getTheme(item.theme);
    const isSubmenuActive = item.submenu.some((sub) => location.pathname.startsWith(sub.to));
    const tooltipId = `ecx-nav-submenu-${item.submenuKey}`;

    const renderPopover = () =>
        collapsed && hoveredSubmenu === item.submenuKey ? (
            <div
                className="position-fixed bg-white border rounded shadow-sm ecx-submenu-popover"
                style={{
                    top: submenuPosition.top,
                    left: submenuPosition.left + 4,
                }}
                onMouseEnter={() => setHoveredSubmenu(item.submenuKey)}
                onMouseLeave={() => setHoveredSubmenu(null)}
            >
                <div className="ecx-submenu-popover__title px-2 pt-2 pb-1">{item.label}</div>
                <ul className="nav nav-pills flex-column p-2 pt-1 gap-1 mb-0">
                    {item.submenu.map((sub) => (
                        <li className="nav-item" key={sub.to}>
                            <SubmenuLink
                                sub={sub}
                                themeKey={item.theme}
                                className={({ isActive }) =>
                                    `nav-link rounded-3 py-2 px-2 fw-medium d-flex align-items-center ${
                                        isActive ? theme.activeClass : theme.idleClass
                                    }`
                                }
                                onClick={() => {
                                    setHoveredSubmenu(null);
                                    onNavigate?.();
                                }}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        ) : null;

    return (
        <li className="nav-item position-relative">
            <button
                type="button"
                className={`nav-link rounded-3 d-flex align-items-center py-2 px-2 w-100 border-0 submenu-toggle fw-medium ${
                    collapsed ? 'justify-content-center' : ''
                } ${isSubmenuActive ? theme.activeClass : theme.idleClass}`}
                onClick={(e) => toggleSubmenu(item.submenuKey, e)}
                onMouseEnter={(e) => {
                    if (collapsed) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setSubmenuPosition({ top: rect.top, left: rect.right });
                        setHoveredSubmenu(item.submenuKey);
                    }
                }}
                aria-expanded={!collapsed && Boolean(openSubmenus[item.submenuKey])}
                data-tooltip-id={collapsed && hoveredSubmenu !== item.submenuKey ? tooltipId : undefined}
                data-tooltip-content={collapsed ? item.label : undefined}
                data-tooltip-place="right"
            >
                <NavIcon icon={item.icon} themeKey={item.theme} collapsed={collapsed} />
                {!collapsed && <span className="flex-fill text-start">{item.label}</span>}
                {!collapsed && (
                    <i
                        className={`bi bi-chevron-down ms-auto ecx-sidebar-submenu-chevron${
                            openSubmenus[item.submenuKey] ? ' is-open' : ''
                        }`}
                        aria-hidden="true"
                    />
                )}
            </button>
            {collapsed && hoveredSubmenu !== item.submenuKey && <Tooltip id={tooltipId} />}
            {renderPopover()}
            {!collapsed && (
                <div
                    className={`ecx-sidebar-submenu${
                        openSubmenus[item.submenuKey] ? ' is-open' : ''
                    }`}
                    aria-hidden={!openSubmenus[item.submenuKey]}
                >
                    <div className="ecx-sidebar-submenu__inner">
                        <ul className="nav nav-pills flex-column gap-1 mb-1">
                            {item.submenu.map((sub) => (
                                <li className="nav-item" key={sub.to}>
                                    <SubmenuLink
                                        sub={sub}
                                        themeKey={item.theme}
                                        className={({ isActive }) =>
                                            `nav-link rounded-3 ecx-sidebar-sublink py-2 px-2 fw-medium d-flex align-items-center ${
                                                isActive ? theme.activeClass : theme.idleClass
                                            }`
                                        }
                                        onClick={onNavigate}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </li>
    );
}

function EcxSidebar({ collapsed = false, mobile = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const logout = useEyeconxStore((state) => state.logout);
    const smsInbox = useEyeconxStore((state) => state.smsInbox);
    const newWebBookingsCount = useEyeconxStore((state) => state.newWebBookingsCount);

    const navBadges = useMemo(
        () => ({
            smsInbox: smsInbox || 0,
            newWebBookingsCount: newWebBookingsCount || 0,
        }),
        [newWebBookingsCount, smsInbox]
    );
    const clinicFeatures = useEyeconxStore((state) => state.clinicFeatures);
    const clinicFeaturesLoaded = useEyeconxStore((state) => state.clinicFeaturesLoaded);
    const sidebarToggled = useEyeconxStore((state) => state.sidebarToggled);
    const sidebarBroken = useEyeconxStore((state) => state.sidebarBroken);
    const setSidebarCollapsed = useEyeconxStore((state) => state.setSidebarCollapsed);
    const toggleSidebar = useEyeconxStore((state) => state.toggleSidebar);
    const setSidebarBroken = useEyeconxStore((state) => state.setSidebarBroken);

    const [openSubmenus, setOpenSubmenus] = useState({});
    const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
    const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });

    const visibleSidebarItems = useMemo(
        () => getVisibleSidebarItems(clinicFeatures, clinicFeaturesLoaded),
        [clinicFeatures, clinicFeaturesLoaded]
    );

    useEffect(() => {
        const handleResize = () => setSidebarBroken(window.innerWidth <= 767);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setSidebarBroken]);

    useEffect(() => {
        setOpenSubmenus((prev) => {
            const next = { ...prev };
            visibleSidebarItems.forEach((item) => {
                if (item.submenu?.some((sub) => location.pathname.startsWith(sub.to))) {
                    next[item.submenuKey] = true;
                }
            });
            return next;
        });
    }, [location.pathname, visibleSidebarItems]);

    const toggleSubmenu = (key, event) => {
        if (collapsed) {
            const rect = event.currentTarget.getBoundingClientRect();
            setSubmenuPosition({ top: rect.top, left: rect.right });
            setHoveredSubmenu((prev) => (prev === key ? null : key));
        } else {
            setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }));
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const closeMobileSidebar = () => {
        if (mobile && sidebarToggled) {
            toggleSidebar();
        }
    };

    const sidebarContent = (
        <div className={`ecx-sidebar-inner ${collapsed ? 'ecx-sidebar--collapsed' : ''}`}>
            <div className="ecx-sidebar-header">
                <div className="ecx-sidebar-logo">
                    {collapsed ? (
                        <span className="ecx-sidebar-logo-compact d-inline-flex align-items-center justify-content-center">
                            E
                        </span>
                    ) : (
                        <img
                            src="/media/logos2/neweyeconxlogo.png"
                            alt="EyeconX"
                            className="img-fluid"
                        />
                    )}
                </div>
                {!mobile && (
                    <button
                        type="button"
                        className="ecx-sidebar-toggle"
                        onClick={() => setSidebarCollapsed(!collapsed)}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={collapsed ? 'Expand menu' : 'Collapse menu'}
                    >
                        <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
                    </button>
                )}
                {mobile && (
                    <button
                        type="button"
                        className="ecx-sidebar-toggle"
                        onClick={toggleSidebar}
                        aria-label="Close navigation menu"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                )}
            </div>

            <hr className="mx-2 my-0" />

            <nav className="ecx-sidebar-nav">
                <ul className="nav nav-pills flex-column mb-auto mt-2 gap-1">
                    {visibleSidebarItems.map((item, idx) => {
                        if (item.type === 'divider') {
                            return (
                                <li className="nav-item" key={`divider-${idx}`}>
                                    {collapsed ? (
                                        <hr className="my-2 mx-1" />
                                    ) : (
                                        <small className={`text-uppercase fw-semibold px-2 py-1 d-block ecx-sidebar-divider ecx-sidebar-divider--${item.accent || 'default'}`}>
                                            {item.label}
                                        </small>
                                    )}
                                </li>
                            );
                        }

                        if (item.submenu) {
                            return (
                                <SubmenuItem
                                    key={item.submenuKey}
                                    item={item}
                                    collapsed={collapsed}
                                    openSubmenus={openSubmenus}
                                    toggleSubmenu={toggleSubmenu}
                                    hoveredSubmenu={hoveredSubmenu}
                                    setHoveredSubmenu={setHoveredSubmenu}
                                    submenuPosition={submenuPosition}
                                    setSubmenuPosition={setSubmenuPosition}
                                    location={location}
                                    onNavigate={mobile ? closeMobileSidebar : undefined}
                                />
                            );
                        }

                        return (
                            <NavItem
                                key={item.to}
                                to={item.to}
                                icon={item.icon}
                                label={item.label}
                                themeKey={item.theme}
                                collapsed={collapsed}
                                badge={item.badgeKey ? navBadges[item.badgeKey] || 0 : 0}
                                onNavigate={mobile ? closeMobileSidebar : undefined}
                            />
                        );
                    })}
                </ul>
            </nav>

            <div className="ecx-sidebar-footer">
                <hr className="mx-1" />
                <ul className="nav nav-pills flex-column mb-2 gap-1">
                    <NavItem
                        to="/whats-new"
                        icon="bi-stars"
                        label="What's New"
                        themeKey="primary"
                        collapsed={collapsed}
                        onNavigate={mobile ? closeMobileSidebar : undefined}
                    />
                    <NavItem
                        to="/help"
                        icon="bi-life-preserver"
                        label="Help & Support"
                        themeKey="primary"
                        collapsed={collapsed}
                        onNavigate={mobile ? closeMobileSidebar : undefined}
                    />
                </ul>
                <button
                    type="button"
                    onClick={handleLogout}
                    className="btn btn-outline-danger ecx-logout-btn w-100 py-2 d-flex align-items-center justify-content-center"
                >
                    <span>Logout</span>
                    <i className="bi bi-box-arrow-right ms-2" />
                </button>

                {!collapsed && (
                    <div className="text-muted text-center mt-3 mb-1">
                        <small className="text-muted" style={{ fontSize: '0.625rem' }}>
                            {new Date().getFullYear()} &copy; EyeconX
                        </small>
                    </div>
                )}
            </div>
        </div>
    );

    if (mobile) {
        if (!sidebarBroken) {
            return null;
        }

        return (
            <>
                {sidebarToggled && (
                    <div
                        className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 ecx-sidebar-mobile-backdrop"
                        onClick={toggleSidebar}
                        aria-hidden="true"
                    />
                )}
                <aside className={`ecx-sidebar ecx-sidebar-mobile-open ${sidebarToggled ? '' : 'd-none'}`}>
                    {sidebarContent}
                </aside>
            </>
        );
    }

    return sidebarContent;
}

export default EcxSidebar;
